import {isTodoExpired, TodoStatus, type Todo} from '@cp/model';
import {mapTimestampToDate} from '@coolcolduk/firebase-util';
import {useEffect, useState} from 'react';
import subscribeTodoCollection from '../../api/todo/subscribeTodoCollection';
import filterTodosBySearch from '../../utils/todo/filterTodosBySearch';

export interface TodoWithExpiration extends Todo {
  isExpired: boolean;
}

interface UseTodosOptions {
  searchQuery?: string;
  showDone?: boolean;
  showArchive?: boolean;
}

export default function useTodos(uid: string | undefined, options: UseTodosOptions = {}) {
  const {searchQuery = '', showDone = true, showArchive = false} = options;
  const [todos, setTodos] = useState<TodoWithExpiration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) {
      setTodos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeTodoCollection(
      uid,
      (list) => {
        const withExpiration: TodoWithExpiration[] = list.map((data) => ({
          ...data,
          isExpired: isTodoExpired(
            data.done != null ? mapTimestampToDate(data.done as never) : null,
            data.repeat ?? null,
          ),
        }));
        setTodos(withExpiration);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [uid]);

  const filtered = todos.filter((todo) => {
    if (!showDone && todo.done !== null) return false;
    if (!showArchive && todo.status === TodoStatus.ARCHIVE) return false;
    return true;
  });

  const searchFiltered = filterTodosBySearch<TodoWithExpiration>(filtered, searchQuery);

  return {
    todos: searchFiltered,
    allTodos: todos,
    loading,
    error,
  };
}
